import React from "react";
import styled from "styled-components";
import { ReactComponent as Check } from "../../assets/icons/CheckSvg.svg";
import PrimaryButton from "../Generic/PrimaryButton";
import StarBadge from "./StarBadge";
import { formatPlanPrice } from "../../helper/planHelper";

const ListElement = ({ text }) => (
  <ListElementWrapper>
    <IconBullet>
      <Check />
    </IconBullet>
    <p>{text}</p>
  </ListElementWrapper>
);

const SubscriptionPlanCard = ({
  planName,
  subtitle,
  benefits,
  price,
  currency,
  interval,
  isContactPlan,
  seatBased,
  currentPlan,
  handleUpgradeSubscription,
  handleContactSales,
}) => {
  const formattedPrice = isContactPlan ? null : formatPlanPrice(price, currency);

  const priceDescription =
    !isContactPlan && seatBased
      ? interval === "year"
        ? "Per seat / year"
        : "Per seat / month"
      : "";

  const buttonText = currentPlan
    ? "Current Plan"
    : isContactPlan
    ? "Contact Sales"
    : "Get started";

  const handleClick = () => {
    if (currentPlan) return;
    if (isContactPlan) {
      handleContactSales?.();
    } else {
      handleUpgradeSubscription?.();
    }
  };

  return (
    <Card>
      <StarWrapper>
        <StarBadge marginBottom={12} />
      </StarWrapper>
      <PlanTitle>{planName}</PlanTitle>
      <PlanSubtitle>{subtitle || "\u00A0"}</PlanSubtitle>
      <PriceWrapper>
        <Price>{formattedPrice || "\u00A0"}</Price>
        <PriceDescription>{priceDescription || "\u00A0"}</PriceDescription>
      </PriceWrapper>
      <Features>
        <CheckList>
          {(benefits || []).map((item, index) => (
            <ListElement text={item} key={index} />
          ))}
        </CheckList>
      </Features>
      <Footer>
        <PrimaryButton
          bgVariant={currentPlan ? "#22C55E" : undefined}
          styled={
            isContactPlan
              ? {
                  width: "100%",
                  padding: "16px",
                  background: "#7F56D9",
                  color: "#FFFFFF",
                  fontSize: "16px",
                  borderRadius: "10px",
                }
              : {
                  width: "100%",
                  padding: "16px",
                  fontSize: "16px",
                  borderRadius: "10px",
                }
          }
          icon={currentPlan ? <Check /> : null}
          text={buttonText}
          onClick={handleClick}
        />
      </Footer>
    </Card>
  );
};

export default SubscriptionPlanCard;

const Card = styled.div`
  display: grid;
  grid-template-rows: subgrid;
  grid-row: span 6;
  background: #FFFFFF;
  border: 1px solid #E9EAEB;
  border-radius: 16px;
  box-shadow:
    0 4px 6px -2px rgba(10, 13, 18, 0.03),
    0 12px 16px -4px rgba(10, 13, 18, 0.08);
  padding: 32px;
  row-gap: 8px;

  @media (max-width: 1300px) {
    padding: 24px;
    min-height: 420px;
  }
  @media (max-width: 1024px) {
    padding: 20px;
    min-height: 380px;
  }
  @media (max-width: 768px) {
    display: flex;
    flex-direction: column;
    grid-row: unset;
    gap: 12px;
    min-height: unset;
  }
`;

const StarWrapper = styled.div`
  display: flex;
  justify-content: center;
  width: 100%;
`;

const PlanTitle = styled.h3`
  color: rgba(24, 29, 39, 1);
  font-family: "Lato", sans-serif;
  font-size: 36px;
  font-weight: 600;
  line-height: 44px;
  letter-spacing: -0.02em;
  text-align: center;
  text-transform: uppercase;
  margin: 0;
  align-self: start;
  overflow-wrap: break-word;
  word-break: break-word;
  width: 100%;

  @media (max-width: 1300px) { font-size: 28px; }
  @media (max-width: 1024px) { font-size: 22px; }
`;

const PlanSubtitle = styled.p`
  color: #667085;
  font-size: 14px;
  font-weight: 400;
  line-height: 20px;
  text-align: center;
  margin: 0;
  align-self: start;
  overflow-wrap: break-word;
  word-break: break-word;
  width: 100%;
`;

const PriceWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 6px;
  align-self: start;
  width: 100%;
`;

const Price = styled.div`
  color: #181D27;
  font-family: "Lato", sans-serif;
  font-size: 48px;
  font-weight: 600;
  line-height: 60px;
  letter-spacing: -0.02em;
  text-align: center;

  @media (max-width: 1300px) { font-size: 38px; }
  @media (max-width: 1024px) { font-size: 32px; }
`;

const PriceDescription = styled.p`
  color: #535862;
  font-family: "Lato", sans-serif;
  font-size: 16px;
  font-weight: 400;
  line-height: 24px;
  text-align: center;
  margin: 0;
`;

const Features = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  align-self: start;
`;

const Footer = styled.div`
  align-self: end;
  margin-left: -32px;
  margin-right: -32px;
  margin-bottom: -32px;
  padding: 32px;
  background: #FAFAFA;
  border-top: 1px solid #F2F4F7;
  border-bottom-left-radius: 16px;
  border-bottom-right-radius: 16px;

  @media (max-width: 1300px) {
    margin-left: -24px;
    margin-right: -24px;
    margin-bottom: -24px;
    padding: 24px;
  }
  @media (max-width: 768px) {
    margin-left: -20px;
    margin-right: -20px;
    margin-bottom: -20px;
    padding: 20px;
  }
`;

const CheckList = styled.ul`
  display: flex;
  flex-direction: column;
  gap: 16px;
  margin: 0;
  padding: 0;
  list-style: none;
`;

const ListElementWrapper = styled.li`
  display: flex;
  align-items: flex-start;
  gap: 12px;
  p {
    font-family: "Lato", sans-serif;
    color: #535862;
    font-size: 16px;
    font-weight: 400;
    line-height: 24px;
    margin: 0;
    max-width: 392px;
    overflow-wrap: break-word;
    word-break: break-word;
  }
`;

const IconBullet = styled.span`
  width: 20px;
  height: 20px;
  min-width: 20px;
  border-radius: 9999px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: #F4EBFF;
  border: 1px solid #E9D7FE;
  color: #7F56D9;
  line-height: 0;

  svg { width: 12px; height: 12px; }
`;